import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { ArrowLeft, ChevronDown, ChevronUp, Clock, Gavel, LogIn, LogOut, Play, RefreshCw, Trophy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { AssignedMatch, GeneratedMatch } from '@/types/courtAssignment';
import { GroupBlock, IndividualMatch } from '@/types/scheduleOrdering';
import ScoreAssignmentDialog from '@/components/tournaments/upcoming/court-assignment/ScoreAssignmentDialog';

interface TournamentParticipant {
  player1_name: string | null;
  player2_name: string | null;
  team_order: number | null;
  category: string | null;
  subcategory: string | null;
  group: string | null;
}

interface JudgeSession {
  judge_id: string;
  judge_name: string;
  court_numbers: number[];
  can_assign_matches: boolean;
  participants: TournamentParticipant[];
  tournament: {
    id: string;
    title: string;
    sport: string;
    group_rules: any;
    match_type: string;
    status: string;
  };
}

interface MatchScore {
  winner: string;
  sets: Array<{ team1: number; team2: number }>;
  score: string;
  duration?: string;
}

interface PendingQueueMatch {
  id: string;
  day: string;
  courtNumber: number;
  courtName: string;
  category: string;
  subcategory: string;
  phase: string;
  group: string;
  time: string;
  match: string;
  // Data needed for assignment
  blockId: string;
  participants: string[];
  participantTimes: any[];
}

const JudgeAccess: React.FC = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [code, setCode] = useState('');
  const [session, setSession] = useState<JudgeSession | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [assignedMatches, setAssignedMatches] = useState<AssignedMatch[]>([]);
  const [pendingQueueMatches, setPendingQueueMatches] = useState<PendingQueueMatch[]>([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [selectedDay, setSelectedDay] = useState<string>('Todos');
  const [assigning, setAssigning] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [dbCompletedResults, setDbCompletedResults] = useState<any[]>([]);

  // Score dialog state
  const [scoreDialogOpen, setScoreDialogOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<{
    match: GeneratedMatch;
    assignedMatch: AssignedMatch;
  } | null>(null);

  const handleValidateCode = async () => {
    if (code.trim().length !== 6) {
      setError('El código debe tener 6 caracteres');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID || 'jvltandqrrpzgjenmnhg';
      const response = await fetch(`https://${projectId}.supabase.co/functions/v1/validate-judge-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_code: code.trim().toUpperCase() }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Código inválido');
      } else {
        // Normalize group_rules if it comes as a JSON string
        if (data.tournament?.group_rules && typeof data.tournament.group_rules === 'string') {
          try {
            data.tournament.group_rules = JSON.parse(data.tournament.group_rules);
          } catch {}
        }
        console.log('🎾 Judge session - group_rules:', data.tournament?.group_rules);
        setSession({ ...data, participants: data.participants || [] });
      }
    } catch {
      setError('Error de conexión');
    }
    setLoading(false);
  };

  // Resolve autoScheduler labels like "G-Masculino 1era-A-AB" to real player names
  const resolveMatchLabel = useCallback((matchLabel: string, participants: TournamentParticipant[]): string => {
    if (matchLabel.includes(' vs ') && !matchLabel.startsWith('G-')) return matchLabel;

    if (matchLabel.startsWith('G-')) {
      const without = matchLabel.slice(2);
      const lastDash = without.lastIndexOf('-');
      if (lastDash === -1) return matchLabel;
      const pairCode = without.slice(lastDash + 1);
      const before = without.slice(0, lastDash);
      const lastDash2 = before.lastIndexOf('-');
      if (lastDash2 === -1) return matchLabel;
      const subcatName = before.slice(0, lastDash2);
      const groupLetter = before.slice(lastDash2 + 1);

      const parts = subcatName.split(' ');
      const category = parts[0];
      const subcategory = parts.slice(1).join(' ');
      const groupName = `Grupo ${groupLetter}`;

      const groupParticipants = participants
        .filter(p => p.category === category && p.subcategory === subcategory && p.group === groupName)
        .sort((a, b) => (a.team_order || 999) - (b.team_order || 999));

      if (groupParticipants.length === 0) return matchLabel;

      if (pairCode.length >= 2) {
        const idx1 = pairCode.charCodeAt(0) - 65;
        const idx2 = pairCode.charCodeAt(1) - 65;
        const team1 = groupParticipants[idx1];
        const team2 = groupParticipants[idx2];

        if (team1 && team2) {
          const name1 = team1.player2_name
            ? `${team1.player1_name} - ${team1.player2_name}`
            : team1.player1_name || 'Equipo';
          const name2 = team2.player2_name
            ? `${team2.player1_name} - ${team2.player2_name}`
            : team2.player1_name || 'Equipo';
          return `${name1} vs ${name2}`;
        }
      }
    }

    return matchLabel;
  }, []);

  const normalizeTeamName = useCallback((name: string): string => {
    return (name || '').toLowerCase().trim().replace(/\s+/g, ' ');
  }, []);

  const buildMatchSignature = useCallback((team1: string, team2: string): string => {
    const t1 = normalizeTeamName(team1);
    const t2 = normalizeTeamName(team2);
    return [t1, t2].sort().join('|||');
  }, [normalizeTeamName]);

  const fetchMatches = useCallback(async () => {
    if (!session) return;
    setLoadingMatches(true);

    try {
      const [scheduleResult, resultsResult] = await Promise.all([
        supabase
          .from('tournament_schedules')
          .select('schedule_data')
          .eq('tournament_id', session.tournament.id)
          .single(),
        supabase
          .from('tournament_match_results')
          .select('team1_player1, team1_player2, team2_player1, team2_player2, court_number, court_name, category, subcategory, phase, group_name, score, sets, winner_team, scheduled_date, completed_at')
          .eq('tournament_id', session.tournament.id)
      ]);

      const concludedSignatures = new Set<string>();
      if (resultsResult.data) {
        for (const r of resultsResult.data) {
          const t1 = [r.team1_player1, r.team1_player2].filter(Boolean).join(' - ');
          const t2 = [r.team2_player1, r.team2_player2].filter(Boolean).join(' - ');
          concludedSignatures.add(buildMatchSignature(t1, t2));
        }
      }

      const judgeCourtResults = (resultsResult.data || [])
        .filter(r => session.court_numbers.includes(r.court_number))
        .sort((a, b) => {
          const dateA = a.completed_at ? new Date(a.completed_at).getTime() : 0;
          const dateB = b.completed_at ? new Date(b.completed_at).getTime() : 0;
          return dateB - dateA;
        });
      setDbCompletedResults(judgeCourtResults);

      if (scheduleResult.data?.schedule_data) {
        const scheduleData = scheduleResult.data.schedule_data as any;
        const blocks: AssignedMatch[] = [];

        // Build a GLOBAL set of block IDs/signatures already taken (assigned anywhere or removed),
        // so pending queue can exclude blocks the organizer moved to OTHER courts or deleted.
        const globallyTakenIds = new Set<string>();
        const globallyTakenSignatures = new Set<string>();
        const buildBlockSignature = (
          category?: string,
          subcategory?: string,
          group?: string,
          phase?: string,
          day?: string,
          startTime?: string,
        ) => `${(category || '').trim().toLowerCase()}|${(subcategory || '').trim().toLowerCase()}|${(group || '').trim().toLowerCase()}|${(phase || '').trim().toLowerCase()}|${(day || '').trim().toLowerCase()}|${(startTime || '').trim()}`;

        if (scheduleData.assignedMatches && Array.isArray(scheduleData.assignedMatches)) {
          for (const am of scheduleData.assignedMatches) {
            if (am?.id) {
              const baseId = String(am.id).startsWith('assigned-') ? String(am.id).slice('assigned-'.length) : String(am.id);
              globallyTakenIds.add(baseId);
              globallyTakenIds.add(String(am.id));
            }
            if (am?.blockId) globallyTakenIds.add(String(am.blockId));
            globallyTakenSignatures.add(buildBlockSignature(am?.category, am?.subcategory, am?.group, am?.phase, am?.day, am?.startTime));
          }
        }

        if (scheduleData.removedMatches && Array.isArray(scheduleData.removedMatches)) {
          for (const rm of scheduleData.removedMatches) {
            if (typeof rm === 'string') {
              globallyTakenIds.add(rm);
            } else if (rm && typeof rm === 'object') {
              if (rm.id) globallyTakenIds.add(String(rm.id));
              if (rm.blockId) globallyTakenIds.add(String(rm.blockId));
              globallyTakenSignatures.add(buildBlockSignature(rm.category, rm.subcategory, rm.group, rm.phase, rm.day, rm.startTime));
            }
          }
        }

        if (scheduleData.assignedMatches && Array.isArray(scheduleData.assignedMatches)) {
          for (const am of scheduleData.assignedMatches) {
            const courtNum = am.courtNumber || parseInt(am.courtName?.replace(/\D/g, '') || '0');
            if (session.court_numbers.includes(courtNum)) {
              blocks.push({
                id: am.id || `block-${courtNum}-${blocks.length}`,
                courtNumber: courtNum,
                courtName: am.courtName || `Cancha ${courtNum}`,
                group: am.group || '',
                category: am.category || '',
                subcategory: am.subcategory || '',
                phase: am.phase || '',
                startTime: am.startTime || '',
                day: am.day || '',
                matches: (am.matches || []).map((m: any, idx: number) => ({
                  id: m.id || `${am.id}-${idx}`,
                  matchNumber: m.matchNumber || idx + 1,
                  matchTime: m.matchTime || '',
                  team1: m.team1 || '',
                  team2: m.team2 || '',
                  status: m.status || 'pending',
                  result: m.result || undefined,
                })),
              });
            }
          }
        }

        setAssignedMatches(blocks);

        const assignedBlockIds = new Set<string>();
        for (const b of blocks) {
          const baseId = b.id.startsWith('assigned-') ? b.id.slice('assigned-'.length) : b.id;
          assignedBlockIds.add(baseId);
        }

        const queue: PendingQueueMatch[] = [];
        const participants = session.participants || [];

        const dayNames: Record<string, string> = {
          'viernes': 'Viernes', 'sabado': 'Sábado', 'domingo': 'Domingo',
          'lunes': 'Lunes', 'martes': 'Martes', 'miercoles': 'Miércoles', 'jueves': 'Jueves'
        };

        for (const [dayKey, dayData] of Object.entries(scheduleData)) {
          if (dayKey === 'assignedMatches' || dayKey === 'removedMatches' || !dayData || typeof dayData !== 'object') continue;
          const dd = dayData as any;
          if (!dd.groupBlocks && !dd.individualMatches) continue;

          const dayLabel = dayNames[dayKey] || dayKey;

          if (dd.groupBlocks && Array.isArray(dd.groupBlocks)) {
            for (const block of dd.groupBlocks as GroupBlock[]) {
              if (!session.court_numbers.includes(block.court)) continue;

              // Skip if this block was already assigned to ANY court (possibly a different one) or removed.
              if (block.id && globallyTakenIds.has(String(block.id))) continue;
              if (globallyTakenSignatures.has(buildBlockSignature(block.category, (block as any).subcategory, block.group, 'Grupos', dayKey, block.startTime))) continue;

              const isAssigned = assignedBlockIds.has(block.id) || blocks.some(b =>
                b.group === block.group && b.category === block.category && b.courtNumber === block.court
              );
              if (isAssigned) continue;

              let blockParticipants = block.participants || [];
              if (blockParticipants.length === 0 && block.matches && block.matches.length > 0) {
                const teamSet = new Set<string>();
                for (const m of block.matches) {
                  const parts = (m.match || '').split(' vs ');
                  parts.forEach(p => { if (p.trim()) teamSet.add(p.trim()); });
                }
                blockParticipants = Array.from(teamSet);
              }

              const resolvedBlockMatches = (block.matches || []).map(m => resolveMatchLabel(m.match || '', participants));
              const allConcluded = resolvedBlockMatches.length > 0 && resolvedBlockMatches.every(matchStr => {
                const parts = matchStr.split(' vs ');
                if (parts.length !== 2) return false;
                return concludedSignatures.has(buildMatchSignature(parts[0], parts[1]));
              });
              if (allConcluded) continue;

              const blockParticipantTimes = (block as any).participantTimes || 
                (block.matches || []).map((m: any, idx: number) => ({
                  matchNumber: idx + 1,
                  matchTime: m.time || '',
                }));

              const matchLabels = resolvedBlockMatches.join(', ');
              queue.push({
                id: block.id || `pending-group-${block.id}`,
                day: dayLabel,
                courtNumber: block.court,
                courtName: `Cancha ${block.court}`,
                category: block.category,
                subcategory: (block as any).subcategory || '',
                phase: 'Grupos',
                group: block.group,
                time: block.startTime,
                match: matchLabels,
                blockId: block.id,
                participants: blockParticipants,
                participantTimes: blockParticipantTimes,
              });
            }
          }

          if (dd.individualMatches && Array.isArray(dd.individualMatches)) {
            for (const im of dd.individualMatches as IndividualMatch[]) {
              if (!session.court_numbers.includes(im.court)) continue;

              // Skip if this individual match was already assigned to ANY court or removed.
              if (im.id && globallyTakenIds.has(String(im.id))) continue;
              if (globallyTakenSignatures.has(buildBlockSignature(im.category, (im as any).subcategory, '', im.phase, dayKey, im.startTime))) continue;

              const isAssigned = assignedBlockIds.has(im.id) || blocks.some(b =>
                b.phase === im.phase && b.category === im.category && b.courtNumber === im.court && b.startTime === im.startTime
              );
              if (isAssigned) continue;

              const resolvedMatch = resolveMatchLabel(im.match || '', participants);

              const parts = resolvedMatch.split(' vs ');
              if (parts.length === 2 && concludedSignatures.has(buildMatchSignature(parts[0], parts[1]))) {
                continue;
              }

              queue.push({
                id: im.id || `pending-ind-${im.court}-${im.startTime}`,
                day: dayLabel,
                courtNumber: im.court,
                courtName: `Cancha ${im.court}`,
                category: im.category,
                subcategory: (im as any).subcategory || '',
                phase: im.phase,
                group: '',
                time: im.startTime,
                match: resolvedMatch,
                blockId: im.id || `ind-${im.court}-${im.startTime}`,
                participants: im.participants || [],
                participantTimes: [],
              });
            }
          }
        }

        setPendingQueueMatches(queue);
      }
    } catch (err) {
      console.error('Error fetching matches:', err);
    }
    setLoadingMatches(false);
  }, [session, resolveMatchLabel, buildMatchSignature]);

  useEffect(() => {
    if (session) {
      fetchMatches();

      const channel = supabase
        .channel(`judge-${session.tournament.id}`)
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'tournament_schedules',
          filter: `tournament_id=eq.${session.tournament.id}`
        }, () => {
          fetchMatches();
        })
        .subscribe();

      return () => { supabase.removeChannel(channel); };
    }
  }, [session, fetchMatches]);

  const getSetsPerMatch = (): number => {
    const rules = (session?.tournament?.group_rules || {}) as any;
    if (rules.setsPerMatch && typeof rules.setsPerMatch === 'number') return rules.setsPerMatch;
    if (rules.setsPerMatch && typeof rules.setsPerMatch === 'string') {
      if (rules.setsPerMatch.includes('3') || rules.setsPerMatch.toLowerCase().includes('mejor')) return 3;
      if (rules.setsPerMatch.includes('1')) return 1;
      const parsed = parseInt(rules.setsPerMatch);
      if (!isNaN(parsed) && (parsed === 1 || parsed === 3)) return parsed;
    }
    return 3;
  };

  const getSuperTieBreak = (): boolean => {
    const rules = (session?.tournament?.group_rules || {}) as any;
    console.log('🎾 Judge - group_rules:', rules, 'superTieBreak value:', rules?.superTieBreak);
    if (typeof rules.superTieBreak === 'boolean') return rules.superTieBreak;
    if (typeof rules.superTieBreak === 'string') return rules.superTieBreak === 'true';
    return false;
  };

  const setsPerMatch = getSetsPerMatch();
  const superTieBreak = getSuperTieBreak();
  const isRelampago = session?.tournament?.match_type === 'relampago';

  const handleSaveScore = async (matchId: string, scoreData: MatchScore) => {
    if (!session || !selectedMatch) return;

    try {
      const am = selectedMatch.assignedMatch;
      const match = selectedMatch.match;

      const numericSets = scoreData.sets.map(s => ({
        team1: Number(s.team1) || 0,
        team2: Number(s.team2) || 0,
      }));

      const winnerTeam = scoreData.winner === 'team1' ? 1 : 2;

      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID || 'jvltandqrrpzgjenmnhg';
      const response = await fetch(`https://${projectId}.supabase.co/functions/v1/judge-save-score`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          judge_id: session.judge_id,
          tournament_id: session.tournament.id,
          match_id: matchId,
          court_number: am.courtNumber,
          court_name: am.courtName,
          team1: match.team1,
          team2: match.team2,
          category: am.category,
          subcategory: am.subcategory,
          phase: am.phase,
          group: am.group,
          sets: numericSets,
          winner_team: winnerTeam,
          score: scoreData.score,
          scheduled_date: (am as any).day || null,
        }),
      });

      if (response.ok) {
        setScoreDialogOpen(false);
        setSelectedMatch(null);
        fetchMatches();
      } else {
        const errData = await response.json();
        alert(errData.error || 'Error al guardar score');
      }
    } catch {
      alert('Error de conexión');
    }
  };

  const handleAssignNextMatch = async () => {
    if (!session || assigning) return;

    const filtered = selectedDay === 'Todos'
      ? pendingQueueMatches
      : pendingQueueMatches.filter(m => m.day === selectedDay);
    
    if (filtered.length === 0) return;
    
    const sorted = [...filtered].sort((a, b) => {
      const dayCompare = (a.day || '').localeCompare(b.day || '');
      if (dayCompare !== 0) return dayCompare;
      return a.time.localeCompare(b.time);
    });
    const nextMatch = sorted[0];

    setAssigning(true);
    try {
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID || 'jvltandqrrpzgjenmnhg';
      const response = await fetch(`https://${projectId}.supabase.co/functions/v1/judge-assign-match`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          judge_id: session.judge_id,
          tournament_id: session.tournament.id,
          block_data: {
            blockId: nextMatch.blockId,
            court: nextMatch.courtNumber,
            group: nextMatch.group,
            category: nextMatch.category,
            subcategory: nextMatch.subcategory,
            phase: nextMatch.phase,
            startTime: nextMatch.time,
            participants: nextMatch.participants,
            participantTimes: nextMatch.participantTimes,
            match: nextMatch.match,
            day: nextMatch.day,
          },
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        alert(data.error || 'Error al asignar partido');
      } else {
        await fetchMatches();
      }
    } catch {
      alert('Error de conexión');
    }
    setAssigning(false);
  };

  // Login screen
  if (!session) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-sm space-y-4">
           <Button variant="ghost" size="sm" onClick={() => navigate('/auth')} className="text-muted-foreground">
             <ArrowLeft className="w-4 h-4 mr-1" /> {t('judge.backToLogin')}
           </Button>
           <Card>
             <CardHeader className="text-center">
               <div className="mx-auto mb-3 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                 <Gavel className="w-6 h-6 text-primary" />
               </div>
               <CardTitle className="text-xl">{t('judge.title')}</CardTitle>
               <p className="text-sm text-muted-foreground">{t('judge.enterCode')}</p>
             </CardHeader>
             <CardContent className="space-y-4">
               <Input
                 value={code}
                 onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 6))}
                 placeholder="ABC123"
                 className="text-center text-2xl tracking-[0.3em] font-mono"
                 maxLength={6}
                 autoFocus
               />
               {error && <p className="text-sm text-destructive text-center">{error}</p>}
               <Button className="w-full" onClick={handleValidateCode} disabled={loading || code.length !== 6}>
                 {loading ? t('judge.validating') : <><LogIn className="w-4 h-4 mr-2" /> {t('auth.login')}</>}
               </Button>
             </CardContent>
           </Card>
        </div>
      </div>
    );
  }

  // Classify blocks by match statuses
  const inCourtBlocks = assignedMatches.filter(b =>
    b.matches.some(m => m.status === 'pending' || m.status === 'in_progress')
  );
  const completedBlocks = assignedMatches.filter(b =>
    b.matches.every(m => m.status === 'completed')
  );

  const renderAssignedBlock = (assignedMatch: AssignedMatch) => {
    const isElimination = assignedMatch.phase !== 'Grupos' && !assignedMatch.phase.toLowerCase().startsWith('grupo');
    const pendingMatches = assignedMatch.matches.filter(m => m.status !== 'completed');
    const displayMatches = pendingMatches.length > 0 ? pendingMatches : assignedMatch.matches;

    return (
      <Card
        key={assignedMatch.id}
        className={`border-l-4 min-h-[200px] ${isElimination ? 'border-l-orange-500 bg-orange-50 dark:bg-orange-950/20' : 'border-l-green-500 bg-green-50 dark:bg-green-950/20'}`}
      >
        <CardContent className="p-3 h-full">
          <Carousel className="w-full h-full">
            <CarouselContent>
              {displayMatches.map((match, index) => (
                <CarouselItem key={match.id}>
                  <div className="space-y-2 h-full flex flex-col justify-between px-6">
                    {/* HEADER */}
                    <div className="flex items-center justify-between">
                      <div className="font-bold text-sm flex items-center gap-2">
                        {assignedMatch.courtName}
                        <span className="text-xs text-muted-foreground font-normal">
                          {match.matchTime || assignedMatch.startTime}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {match.matchNumber}/{assignedMatch.matches.length}
                      </div>
                    </div>

                    {/* TEAMS */}
                    <div className="text-xs space-y-1 text-left">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{match.team1}</span>
                        <span className="text-muted-foreground">vs</span>
                      </div>
                      <div>
                        <span className="font-medium">{match.team2}</span>
                      </div>
                    </div>

                    {/* BADGES */}
                    <div className="flex items-start gap-1 justify-start flex-wrap">
                      {assignedMatch.phase && isElimination && (
                        <Badge variant="default" className="text-xs py-0 bg-orange-500 hover:bg-orange-600">
                          {assignedMatch.phase}
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-xs py-0">{assignedMatch.category}</Badge>
                      {assignedMatch.phase === 'Grupos' && assignedMatch.group && (
                        <Badge variant="outline" className="text-xs py-0 text-blue-600">{assignedMatch.group}</Badge>
                      )}
                    </div>

                    {/* SCORE BUTTON */}
                    {match.status !== 'completed' && (
                      <div className="mt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full text-xs flex items-center justify-center gap-1"
                          onClick={() => {
                            setSelectedMatch({ match, assignedMatch });
                            setScoreDialogOpen(true);
                          }}
                        >
                           <Trophy className="w-3 h-3" />
                           {t('judge.assignScore')}
                        </Button>
                      </div>
                    )}

                    {/* COMPLETED SCORE DISPLAY */}
                    {match.status === 'completed' && match.result && (
                      <div className="text-xs text-primary font-mono text-center">
                        {match.result.sets?.map((s: any) => `${s.team1}-${s.team2}`).join(', ')}
                      </div>
                    )}

                    {/* DOTS */}
                    {displayMatches.length > 1 && (
                      <div className="flex justify-center gap-1 mt-2">
                        {displayMatches.map((_, dotIndex) => (
                          <div
                            key={dotIndex}
                            className={`w-1.5 h-1.5 rounded-full ${dotIndex === index ? 'bg-foreground' : 'bg-muted-foreground/30'}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            {displayMatches.length > 1 && <CarouselPrevious className="left-1 h-4 w-4" />}
            {displayMatches.length > 1 && <CarouselNext className="right-1 h-4 w-4" />}
          </Carousel>
        </CardContent>
      </Card>
    );
  };

  const renderEmptyState = (message: string) => (
    <Card>
      <CardContent className="py-8 text-center text-sm text-muted-foreground">
        {message}
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-background p-4 max-w-lg mx-auto">
      {/* Header */}
      <Card className="mb-4">
        <CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <Gavel className="w-5 h-5 text-primary" />
            </div>
            <div className="min-w-0">
              <h1 className="font-semibold text-sm truncate">{session.tournament.title}</h1>
              <p className="text-xs text-muted-foreground">{session.judge_name}</p>
              <div className="flex gap-1 mt-1 flex-wrap">
                {session.court_numbers.map(cn => (
                  <Badge key={cn} variant="secondary" className="text-xs">{t('judge.court')} {cn}</Badge>
                ))}
              </div>
            </div>
            <div className="flex gap-1 ml-auto shrink-0">
              <Button variant="ghost" size="icon" onClick={fetchMatches}>
                <RefreshCw className={`w-4 h-4 ${loadingMatches ? 'animate-spin' : ''}`} />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => setSession(null)} className="text-destructive hover:text-destructive">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Match blocks */}
      <Tabs defaultValue="en-cancha" className="mb-4">
        <TabsList className="w-full">
          <TabsTrigger value="en-cancha" className="flex-1">
            {language === 'en' ? 'On Court' : 'En Cancha'} ({inCourtBlocks.length})
          </TabsTrigger>
          <TabsTrigger value="pendientes" className="flex-1">
            {t('judge.pending')} ({pendingQueueMatches.length})
          </TabsTrigger>
        </TabsList>
        <TabsContent value="en-cancha">
          {inCourtBlocks.length === 0
            ? (session.can_assign_matches && pendingQueueMatches.length > 0
              ? (
                <Card>
                  <CardContent className="py-8 flex flex-col items-center gap-4">
                    <p className="text-sm text-muted-foreground">{language === 'en' ? 'No matches on court' : 'No hay partidos en cancha'}</p>
                    <Button
                      onClick={handleAssignNextMatch}
                      disabled={assigning}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      {assigning ? (language === 'en' ? 'Assigning...' : 'Asignando...') : <><Play className="w-4 h-4 mr-2" /> {language === 'en' ? 'Assign next match' : 'Asignar siguiente partido'}</>}
                    </Button>
                  </CardContent>
                </Card>
              )
              : renderEmptyState('No hay partidos en cancha actualmente')
            )
            : <div className="space-y-3">{inCourtBlocks.map(renderAssignedBlock)}</div>}
        </TabsContent>
        <TabsContent value="pendientes">
          {(() => {
            const uniqueDays = Array.from(new Set(pendingQueueMatches.map(m => m.day))).sort();
            const filteredPending = selectedDay === 'Todos'
              ? pendingQueueMatches
              : pendingQueueMatches.filter(m => m.day === selectedDay);
            const showDayFilter = uniqueDays.length >= 1;

            return pendingQueueMatches.length === 0
              ? renderEmptyState('No hay partidos pendientes en cola')
              : (
                <div className="space-y-3">
                  {showDayFilter && (
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {['Todos', ...uniqueDays].map(day => (
                        <Button
                          key={day}
                          size="sm"
                          variant={selectedDay === day ? 'default' : 'outline'}
                          className="text-xs shrink-0"
                          onClick={() => setSelectedDay(day)}
                        >
                          {day} ({day === 'Todos' ? pendingQueueMatches.length : pendingQueueMatches.filter(m => m.day === day).length})
                        </Button>
                      ))}
                    </div>
                  )}
                  {filteredPending.length === 0
                    ? renderEmptyState('No hay partidos pendientes para este día')
                    : isRelampago
                      ? (
                        <div className="space-y-2">
                          {filteredPending
                            .sort((a, b) => {
                              const dayCompare = (a.day || '').localeCompare(b.day || '');
                              if (dayCompare !== 0) return dayCompare;
                              return a.time.localeCompare(b.time);
                            })
                    .map((pm) => (
                      <Card key={pm.id} className="border-l-4 border-l-muted-foreground/30">
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between mb-1">
                            <div className="font-bold text-sm flex items-center gap-2">
                              {pm.courtName}
                              <span className="text-xs text-muted-foreground font-normal">{pm.day}</span>
                            </div>
                            <div className="flex items-center gap-1 text-sm font-semibold text-primary">
                              <Clock className="w-3 h-3" />
                              {pm.time}
                            </div>
                          </div>
                          <div className="text-xs space-y-0.5 mb-2">
                            {pm.match.includes(' vs ') ? (() => {
                              const teams = pm.match.split(' vs ');
                              return (
                                <>
                                  <div className="font-medium">{teams[0]}</div>
                                  <div className="text-muted-foreground">vs</div>
                                  <div className="font-medium">{teams[1]}</div>
                                </>
                              );
                            })() : <div className="font-medium">{pm.match}</div>}
                          </div>
                          <div className="flex items-center gap-1 flex-wrap">
                            <Badge variant="outline" className="text-xs py-0">{pm.category}</Badge>
                            <Badge variant="outline" className="text-xs py-0">{pm.phase}</Badge>
                            {pm.group && (
                              <Badge variant="outline" className="text-xs py-0 text-blue-600">{pm.group}</Badge>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                </div>
              )
                      : (() => {
                        // Circuito/Round Robin: group pending matches by court+group+category+day
                        const grouped = new Map<string, PendingQueueMatch[]>();
                        const individualPendingItems: PendingQueueMatch[] = [];

                        for (const pm of filteredPending) {
                          if (pm.phase === 'Grupos' && pm.group) {
                            const key = `${pm.courtName}-${pm.group}-${pm.category}-${pm.day}`;
                            if (!grouped.has(key)) grouped.set(key, []);
                            grouped.get(key)!.push(pm);
                          } else {
                            individualPendingItems.push(pm);
                          }
                        }

                        const groupedBlocks = Array.from(grouped.entries())
                          .map(([key, matches]) => ({ key, matches: matches.sort((a, b) => a.time.localeCompare(b.time)) }))
                          .sort((a, b) => a.matches[0].time.localeCompare(b.matches[0].time));

                        return (
                          <div className="space-y-3">
                            {groupedBlocks.map(({ key, matches }) => {
                              // Flatten all individual match strings from all grouped PendingQueueMatch items
                              const refMatch = matches[0];
                               const individualMatches: { matchStr: string; pm: PendingQueueMatch; matchIndex: number }[] = [];
                               for (const pm of matches) {
                                 const parts = pm.match.split(', ').filter(Boolean);
                                 parts.forEach((part, idx) => {
                                   individualMatches.push({ matchStr: part, pm, matchIndex: idx });
                                 });
                               }
                              const totalSlides = individualMatches.length;

                              return (
                                <Card key={key} className="border-l-4 border-l-muted-foreground/30">
                                  <CardContent className="p-3">
                                    <Carousel className="w-full">
                                      <CarouselContent>
                                         {individualMatches.map(({ matchStr, pm, matchIndex }, index) => {
                                           const matchTime = pm.participantTimes?.[matchIndex]?.matchTime || pm.time;
                                           return (
                                           <CarouselItem key={`${pm.id}-${index}`}>
                                             <div className="space-y-2 flex flex-col justify-start px-6">
                                               <div className="flex items-center justify-between">
                                                 <div className="font-bold text-sm flex items-center gap-2">
                                                   {refMatch.courtName}
                                                   <span className="text-xs text-muted-foreground font-normal">{refMatch.day}</span>
                                                 </div>
                                                 <div className="text-xs text-muted-foreground">
                                                   {matchTime && <span className="text-sm font-semibold text-primary">{matchTime}</span>}
                                                  <span className="ml-2">{index + 1}/{totalSlides}</span>
                                                </div>
                                              </div>
                                              <div className="text-xs space-y-1 text-left">
                                                {matchStr.includes(' vs ') ? (() => {
                                                  const teams = matchStr.split(' vs ');
                                                  return (
                                                    <>
                                                      <div className="flex items-center gap-2">
                                                        <span className="font-medium">{teams[0]}</span>
                                                        <span className="text-muted-foreground">vs</span>
                                                      </div>
                                                      <div><span className="font-medium">{teams[1]}</span></div>
                                                    </>
                                                  );
                                                })() : <div className="font-medium">{matchStr}</div>}
                                              </div>
                                              <div className="flex items-start gap-1 justify-start flex-wrap">
                                                <Badge variant="outline" className="text-xs py-0">{refMatch.category}</Badge>
                                                <Badge variant="outline" className="text-xs py-0">{refMatch.phase}</Badge>
                                                {refMatch.group && (
                                                  <Badge variant="outline" className="text-xs py-0 text-blue-600">{refMatch.group}</Badge>
                                                )}
                                              </div>
                                              {totalSlides > 1 && (
                                                <div className="flex justify-center gap-1 mt-2">
                                                  {individualMatches.map((_, dotIndex) => (
                                                    <div
                                                      key={dotIndex}
                                                      className={`w-1.5 h-1.5 rounded-full ${dotIndex === index ? 'bg-foreground' : 'bg-muted-foreground/30'}`}
                                                    />
                                                  ))}
                                                </div>
                                              )}
                                            </div>
                                           </CarouselItem>
                                           );
                                         })}
                                      </CarouselContent>
                                      {totalSlides > 1 && <CarouselPrevious className="left-1 h-4 w-4" />}
                                      {totalSlides > 1 && <CarouselNext className="right-1 h-4 w-4" />}
                                    </Carousel>
                                  </CardContent>
                                </Card>
                              );
                            })}
                            {individualPendingItems
                              .sort((a, b) => a.time.localeCompare(b.time))
                              .map((pm) => (
                                <Card key={pm.id} className="border-l-4 border-l-muted-foreground/30">
                                  <CardContent className="p-3">
                                    <div className="flex items-center justify-between mb-1">
                                      <div className="font-bold text-sm flex items-center gap-2">
                                        {pm.courtName}
                                        <span className="text-xs text-muted-foreground font-normal">{pm.day}</span>
                                      </div>
                                      <div className="flex items-center gap-1 text-sm font-semibold text-primary">
                                        <Clock className="w-3 h-3" />
                                        {pm.time}
                                      </div>
                                    </div>
                                    <div className="text-xs space-y-0.5 mb-2">
                                      {pm.match.includes(' vs ') ? (() => {
                                        const teams = pm.match.split(' vs ');
                                        return (
                                          <>
                                            <div className="font-medium">{teams[0]}</div>
                                            <div className="text-muted-foreground">vs</div>
                                            <div className="font-medium">{teams[1]}</div>
                                          </>
                                        );
                                      })() : <div className="font-medium">{pm.match}</div>}
                                    </div>
                                    <div className="flex items-center gap-1 flex-wrap">
                                      <Badge variant="outline" className="text-xs py-0">{pm.category}</Badge>
                                      <Badge variant="outline" className="text-xs py-0">{pm.phase}</Badge>
                                    </div>
                                  </CardContent>
                                </Card>
                              ))}
                          </div>
                        );
                      })()
                  }
                </div>
              );
          })()}
        </TabsContent>
      </Tabs>

      {/* Completed — sourced from tournament_match_results DB table */}
      {dbCompletedResults.length > 0 && (
        <div className="space-y-3">
          <Button
            variant="outline"
            size="sm"
            className="w-full flex items-center justify-center gap-2 text-sm font-semibold"
            onClick={() => setShowCompleted(!showCompleted)}
          >
            {showCompleted ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {t('judge.completed')} ({dbCompletedResults.length})
          </Button>
          {showCompleted && (
            <div className="space-y-3">
              {dbCompletedResults.map((result, idx) => {
                const team1 = [result.team1_player1, result.team1_player2].filter(Boolean).join(' - ');
                const team2 = [result.team2_player1, result.team2_player2].filter(Boolean).join(' - ');
                const isElim = result.phase !== 'Grupos' && !result.phase?.toLowerCase().startsWith('grupo');
                return (
                  <Card
                    key={`db-result-${idx}`}
                    className={`border-l-4 opacity-75 ${isElim ? 'border-l-orange-500 bg-orange-50 dark:bg-orange-950/20' : 'border-l-green-500 bg-green-50 dark:bg-green-950/20'}`}
                  >
                    <CardContent className="p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="secondary" className="text-xs">{result.court_name || `Cancha ${result.court_number}`}</Badge>
                          <Badge variant="outline" className="text-xs">{result.category}</Badge>
                          {result.subcategory && <Badge variant="outline" className="text-xs">{result.subcategory}</Badge>}
                          <Badge variant="outline" className="text-xs">{result.phase}</Badge>
                          {result.group_name && <Badge variant="outline" className="text-xs">{result.group_name}</Badge>}
                        </div>
                        <Trophy className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <div className="text-sm space-y-1">
                        <div className={`font-medium ${result.winner_team === 1 ? 'text-green-700 dark:text-green-400' : ''}`}>
                          {team1} {result.winner_team === 1 && '✓'}
                        </div>
                        <div className="text-muted-foreground text-xs">vs</div>
                        <div className={`font-medium ${result.winner_team === 2 ? 'text-green-700 dark:text-green-400' : ''}`}>
                          {team2} {result.winner_team === 2 && '✓'}
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground font-semibold">
                        Score: {result.score}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Score Assignment Dialog */}
      {selectedMatch && (
        <ScoreAssignmentDialog
          open={scoreDialogOpen}
          onOpenChange={setScoreDialogOpen}
          match={selectedMatch.match}
          assignedMatch={selectedMatch.assignedMatch}
          setsPerMatch={setsPerMatch}
          superTieBreak={superTieBreak}
          isRelampago={isRelampago}
          compactMode={true}
          onSaveScore={handleSaveScore}
        />
      )}
    </div>
  );
};

export default JudgeAccess;
